import type { NuxtApp } from '#app'
import { useLogger } from '~/composables/useLogger'
import { resetAccountNotifications } from '~/composables/resetAccountNotifications'
import { resetStepUp } from '~/composables/resetStepUp'
import { resetSetupIntent } from '~/composables/resetSetupIntent'
import { revokeStripeLinkTrust } from '~/composables/revokeStripeLinkTrust'
import { useDashboardQuickActionsStore } from '~/stores/useDashboardQuickActionsStore'
import { identityGeneration } from '~/utils/identityGeneration'
import { pendingTwoFactorChallenge } from '~/utils/pendingTwoFactorChallenge'
import { sanctumTokenStorage } from '~/utils/sanctumTokenStorage'

const logger = useLogger('IdentityReset')

interface ResetStep {
  name: string
  run: () => void | Promise<void>
}

interface IdentityResetResult {
  cleared: boolean
  failedSteps: string[]
}

// Every step assigns fixed defaults, so repeated or concurrent runs converge; each is isolated so one throwing step cannot skip the rest.
export const resetAccountScopedState = async (
  nuxtApp: NuxtApp,
  options: { clearToken: boolean; departingIdentityId?: number | null } = { clearToken: true }
): Promise<IdentityResetResult> => {
  // Before any step: a response already in flight under the previous generation must not write back into the caches cleared below.
  identityGeneration.bump()

  const steps: ResetStep[] = [
    // Must stay first: the rejected bearer credential has to be unreadable before any later step can trigger a request that reuses it.
    {
      name: 'sanctumToken',
      run: () => {
        if (!options.clearToken) return undefined
        return sanctumTokenStorage.set(nuxtApp, undefined)
      }
    },
    // Cleared early so a slow later step cannot leave them usable past the identity transition.
    {
      name: 'stripeSetupIntent',
      run: () => {
        resetSetupIntent()
      }
    },
    {
      name: 'stripeLinkTrust',
      run: () => revokeStripeLinkTrust(options.departingIdentityId ?? null)
    },
    {
      name: 'userStore',
      run: () => {
        useUserStore().clearUserState()
      }
    },
    {
      name: 'notifications',
      run: () => {
        resetAccountNotifications(nuxtApp)
      }
    },
    {
      name: 'searchAccountState',
      run: () => {
        useSearchStore().clearAccountScopedState()
      }
    },
    {
      name: 'cart',
      run: () => {
        useCartStore().resetCart()
      }
    },
    {
      name: 'dashboardStoreCache',
      run: () => {
        useDashboardStore().clearStoreCache()
      }
    },
    {
      name: 'dashboardQuickActions',
      run: () => {
        useDashboardQuickActionsStore().reset()
      }
    },
    {
      name: 'menuSupportContactInfo',
      run: () => {
        useMenuSupportStore().clearContactInfo()
      }
    },
    {
      name: 'pendingTwoFactorChallenge',
      run: () => {
        pendingTwoFactorChallenge.clear()
      }
    },
    // A step-up confirmation authorizes one account's action; the next account replays nothing.
    {
      name: 'stepUp',
      run: () => {
        resetStepUp()
      }
    }
  ]

  const failedSteps: string[] = []
  for (const step of steps) {
    try {
      await step.run()
    } catch (error: unknown) {
      failedSteps.push(step.name)
      logger.error('resetAccountScopedState', {
        step: step.name,
        errorName: error instanceof Error ? error.name : 'UnknownError'
      })
    }
  }

  return { cleared: failedSteps.length === 0, failedSteps }
}
